const byoPersonalizationManager = {
  inlineSelectionState: new WeakMap(),
  composingInputs: new WeakSet(),

  // Tell mobile keyboards how to capitalize text for the currently selected case.
  // handles CASE buttons for editable divs
  updateAutocapitalize: function (input) {
    let autocapitalize = 'sentences';

    if ( input.dataset.case === 'lowercase' ) {
      autocapitalize = 'none';
    } else if ( input.dataset.case === 'uppercase' ) {
      autocapitalize = 'characters';
    }

    input.setAttribute('autocapitalize', autocapitalize);
  },

  // Force heart characters to use their monochrome text presentation on Apple devices.
  // prevent the Apple red heart emoji
  normalizeTextHearts: function (value) {
    const textHeart = '\u2764\uFE0E';
    const suitHeart = '\u2665\uFE0E';
    let normalizedValue = value;

    if ( typeof normalizedValue !== 'string' || normalizedValue.length === 0 ) {
      return normalizedValue;
    }

    normalizedValue = normalizedValue.replace(/\u2764(?:\uFE0F|\uFE0E)?/g, textHeart);
    normalizedValue = normalizedValue.replace(/\u2665(?:\uFE0F|\uFE0E)?/g, suitHeart);

    return normalizedValue;
  },

  // Read text consistently from a normal input or an inline contenteditable field.
  getInputValue: function (input) {
    if ( input.dataset.byoInlineEditor !== 'true' ) {
      return input.value || '';
    }

    let textValue = '';

    for ( const childNode of input.childNodes ) {
      if ( childNode.nodeType === Node.TEXT_NODE ) {
        textValue += childNode.textContent || '';
      } else if ( childNode.nodeType === Node.ELEMENT_NODE && childNode.nodeName !== 'IMG' ) {
        textValue += childNode.textContent || '';
      }
    }

    return textValue;
  },

  // Write text consistently to a normal input or an inline contenteditable field.
  setInputValue: function (input, textValue) {
    const normalizedValue = textValue || '';

    if ( input.dataset.byoInlineEditor === 'true' ) {
      input.textContent = normalizedValue;
    } else {
      input.value = normalizedValue;
    }
  },

  // Check whether a personalization field contains text or an inline image token.
  inputHasPersonalization: function (input) {
    const inputValue = this.getInputValue(input).replace(/\u00A0/g, ' ').trim();
    let hasInlineImage = false;

    if ( input.dataset.byoInlineEditor === 'true' ) {
      hasInlineImage = input.querySelector('img') !== null;
    }

    const hasPersonalization = inputValue.length > 0 || hasInlineImage;

    return hasPersonalization;
  },

  // Check whether the variant currently selected in the modal can be purchased.
  selectedVariantIsAvailable: function (personalization) {
    const modal = personalization.closest('x-modal');
    const variantInput = modal?.querySelector('[data-upsell-variant-id]');
    const variantsScript = modal?.querySelector('[data-upsell-variants]');
    const selectedVariantId = String(variantInput?.value || '');
    let variants = [];

    try {
      variants = JSON.parse(variantsScript?.textContent || '[]');
    } catch (error) {
      variants = [];
    }

    for ( const variant of variants ) {
      if ( String(variant.id) === selectedVariantId ) {
        return variant.available === true;
      }
    }

    return false;
  },

  // Apply the drawer's required-field rules to the modal Add/Update item button.
  validateForm: function (personalization) {
    const modal = personalization.closest('x-modal');
    const addToBundleButton = modal?.querySelector('[data-byo-add-to-bundle]');

    if ( !addToBundleButton ) {
      return false;
    }

    let isValid = personalization.dataset.byoValidationRequired !== 'true';

    if ( personalization.dataset.byoValidationRequired === 'true' ) {
      const isOptionalProduct = personalization.dataset.personalizedOptional === 'true';
      const allInputs = personalization.querySelectorAll('[data-byo-personalization-input]');
      const frontInputs = personalization.querySelectorAll('[data-byo-personalization-input][data-side="front"]');

      if ( isOptionalProduct ) {
        isValid = false;

        for ( const input of allInputs ) {
          if ( this.inputHasPersonalization(input) ) {
            isValid = true;
            break;
          }
        }
      } else {
        isValid = true;

        for ( const input of frontInputs ) {
          if ( !this.inputHasPersonalization(input) ) {
            isValid = false;
            break;
          }
        }

        if ( isValid ) {
          const frontGemstoneFields = personalization.querySelectorAll('[data-byo-gemstone-field][data-side="front"], [data-byo-circle-gemstone][data-side="front"]');

          for ( const gemstoneField of frontGemstoneFields ) {
            const selectedGemstone = gemstoneField.querySelector('[data-personalization-type="gemstone"][aria-selected="true"]');

            if ( !selectedGemstone ) {
              isValid = false;
              break;
            }
          }
        }
      }
    }

    const isAvailable = this.selectedVariantIsAvailable(personalization);
    addToBundleButton.disabled = !isValid || !isAvailable;
    personalization.dataset.byoPersonalizationValid = String(isValid);

    return isValid;
  },

  // Read JSON from a script inside one personalization form.
  readJson: function (personalization, selector, fallback) {
    try {
      const scriptElement = personalization.querySelector(selector);
      let scriptContent = "";
      if ( scriptElement ) {
        scriptContent = scriptElement.textContent;
      }
      const parsedJson = JSON.parse(scriptContent) || fallback;

      return parsedJson
    } catch (error) {
      return fallback;
    }
  },

  // Convert a comma-separated metafield setting into the order configured in theme settings.
  getSelectedValues: function (value, orderValue = '') {
    if ( !value ) {
      const emptyValues = [];
      return emptyValues;
    }

    const values = String(value).split(',');
    const order = String(orderValue || '').split('||');
    const cleanedValues = [];
    const cleanedOrder = [];

    for ( const item of values ) {
      const cleanedItem = item.trim().toLowerCase();

      if ( cleanedItem ) {
        cleanedValues.push(cleanedItem);
      }
    }

    for ( const item of order ) {
      const itemWithoutPropertyCode = item.split('[')[0];
      const cleanedItem = itemWithoutPropertyCode.trim().toLowerCase();

      if ( cleanedItem ) {
        cleanedOrder.push(cleanedItem);
      }
    }

    cleanedValues.sort((first, second) => {
      const firstIndex = cleanedOrder.indexOf(first);
      const secondIndex = cleanedOrder.indexOf(second);
      let sortPosition = firstIndex - secondIndex;

      if ( firstIndex < 0 && secondIndex < 0 ) {
        sortPosition = 0;
      } else if ( firstIndex < 0 ) {
        sortPosition = 1;
      } else if ( secondIndex < 0 ) {
        sortPosition = -1;
      }

      return sortPosition;
    });

    return cleanedValues;
  },

  // Find the option object that matches a configured metafield value.
  findOption: function (options, value) {
    if (  !Array.isArray(options) ) {
      return null;
    }

    for ( const option of options ) {
      if ( String(option?.value || '').trim().toLowerCase() === value ) {
        return option;
      }
    }

    return null;
  },

  // Find the front or back configuration for the selected variant options.
  findSideData: function (sideData, currentOptions) {
    if (  !sideData || typeof sideData !== 'object' ) {
      const emptySideData = [];
      return emptySideData;
    }

    const fullOptionName = currentOptions.join(' / ').trim().toLowerCase();
    const colorName = String(currentOptions[0] || '').trim().toLowerCase();

    for ( const key in sideData ) {
      const normalizedKey = key.trim().toLowerCase();

      if ( normalizedKey === fullOptionName || normalizedKey === colorName ) {
        const matchingSideData = Array.isArray(sideData[key]) ? sideData[key] : [];
        return matchingSideData;
      }
    }

    const emptySideData = [];
    return emptySideData;
  },

  // Check whether one side of the metafield contains any usable form components.
  hasPersonalizationComponents: function (sideData) {
    if ( !sideData || typeof sideData !== 'object' ) {
      return false;
    }

    for ( const optionName in sideData ) {
      const components = sideData[optionName];

      if ( Array.isArray(components) && components.length > 0 ) {
        return true;
      }
    }

    return false;
  },

  // Treat a Shopify option whose complete value is "No" as opting out of personalization.
  // Sports charms are a good example
  hasPersonalizationOptOut: function (currentOptions) {
    for ( const optionValue of currentOptions ) {
      const normalizedValue = String(optionValue || '').trim().toLowerCase();

      if ( normalizedValue === 'no' ) {
        return true;
      }
    }

    return false;
  },

  // Return the currently selected Shopify option values from the modal.
  getCurrentOptions: function (personalization) {
    const modal = personalization.closest('x-modal');
    const checkedInputs = modal?.querySelectorAll('[data-option-position]:checked') || [];
    const optionsByPosition = [];

    for ( const input of checkedInputs ) {
      optionsByPosition[Number(input.dataset.optionPosition) - 1] = input.value;
    }

    const selectedOptions = optionsByPosition.filter((value) => value != null);

    if ( selectedOptions.length > 0 ) {
      return selectedOptions;
    }

    try {
      const savedOptions = personalization.dataset.selectedOptions || '[]';
      const parsedOptions = JSON.parse(savedOptions);
      return parsedOptions;
    } catch (error) {
      const emptyOptions = [];
      return emptyOptions;
    }
  },

  // Return the existing drawer engine that matches this product's personalization version.
  getDrawerManager: function (personalization) {
    const version = personalization.dataset.byoPersonalizationVersion === 'v1' ? 'v1' : 'latest';
    const drawerManagers = window.personalizationManagers || {};
    const drawerManager = drawerManagers[version] || null;
    return drawerManager;
  },

  // Find the image component used as the Fabric background for one product side.
  findPreviewImage: function (components) {
    let imageData = null;

    for ( const component of components ) {
      if ( component?.type !== 'image' ) {
        continue;
      }

      const configuredImage = component?.field_options?.upload_image?.options?.image || null;

      if ( configuredImage?.value ) {
        imageData = configuredImage;
        break;
      }
    }

    return imageData;
  },

  // Use the drawer's 600px coordinate system; CSS scales the finished preview to 400px.
  configureDrawerManager: function (drawerManager) {
    const fontSizingScript = document.querySelector('#item-font-sizing');

    drawerManager.canvasSize = 600;
    drawerManager.debug = false;

    if ( fontSizingScript ) {
      try {
        const fontSizing = JSON.parse(fontSizingScript.textContent || '{}');
        drawerManager.fontSizing = fontSizing;
      } catch (error) {
        drawerManager.fontSizing = {};
      }
    }
  },

  // Remove Fabric instances and adapter markup before rebuilding a modal preview.
  disposePreviews: function (personalization) {
    const state = personalization.byoPreviewState || null;

    if ( state ) {
      for ( const sideName of ['front', 'back'] ) {
        const sideState = state[sideName] || null;

        if ( sideState?.canvas ) {
          sideState.canvas.dispose();
        }

        if ( state.drawerManager?.fabricMap?.[sideState?.editorId] ) {
          delete state.drawerManager.fabricMap[sideState.editorId];
        }
      }
    }

    const adapters = personalization.querySelector('[data-byo-preview-adapters]');

    if ( adapters ) {
      adapters.remove();
    }

    delete personalization.byoPreviewState;
    this.setPreviewAvailability(personalization, false, false);
  },

  // Show only the product slideshow or the requested Fabric preview panel.
  showPreview: function (personalization, viewName) {
    const modal = personalization.closest('x-modal');
    const previewRoot = modal?.querySelector('[data-byo-preview-root]');

    if ( !previewRoot ) {
      return;
    }

    const gallery = previewRoot.querySelector('.upsell-quick-add-modal__gallery');
    const galleryControls = previewRoot.querySelectorAll(':scope > carousel-prev-button, :scope > carousel-next-button, :scope > [data-byo-gallery-dots]');
    const panels = previewRoot.querySelectorAll('[data-byo-preview-panel]');
    const tabs = previewRoot.querySelectorAll('[data-byo-preview-tab]');

    if ( gallery ) {
      gallery.hidden = viewName !== 'product';
    }

    for ( const galleryControl of galleryControls ) {
      galleryControl.hidden = viewName !== 'product';
    }

    for ( const panel of panels ) {
      panel.hidden = panel.dataset.byoPreviewPanel !== viewName;
    }

    for ( const tab of tabs ) {
      const isSelected = tab.dataset.byoPreviewTab === viewName;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    }

    previewRoot.dataset.byoPreviewView = viewName;
  },

  // Reveal only the Front and Back tabs that have configured preview images.
  setPreviewAvailability: function (personalization, hasFront, hasBack) {
    const modal = personalization.closest('x-modal');
    const previewRoot = modal?.querySelector('[data-byo-preview-root]');
    const tabsWrapper = previewRoot?.querySelector('[data-byo-preview-tabs]');
    const frontTab = previewRoot?.querySelector('[data-byo-preview-tab="front"]');
    const backTab = previewRoot?.querySelector('[data-byo-preview-tab="back"]');

    if ( !previewRoot || !tabsWrapper || !frontTab || !backTab ) {
      return;
    }

    frontTab.hidden = !hasFront;
    backTab.hidden = !hasBack;
    tabsWrapper.hidden = !hasFront && !hasBack;

    const currentView = previewRoot.dataset.byoPreviewView || 'product';
    const currentViewIsUnavailable = (currentView === 'front' && !hasFront) || (currentView === 'back' && !hasBack);

    if ( currentViewIsUnavailable || (!hasFront && !hasBack) ) {
      this.showPreview(personalization, 'product');
    }
  },

  // Build the hidden drawer-compatible element used by the shared Fabric renderer.
  createFabricAdapter: function (personalization, input, drawerManager, editorId) {
    const adapter = document.createElement('div');
    const settings = JSON.parse(input.dataset.byoFabricSettings || '{}');
    const lowerLimit = Number(settings.lowerLimit || 0);
    const upperLimit = Number(settings.upperLimit || 0);
    const mixedLimit = Number(settings.mixedLimit || 0);
    const longestLimit = Math.max(lowerLimit, upperLimit, mixedLimit, 1);
    const fabricProps = settings.fabricProps || {};
    const placeholder = settings.placeholder || 'M'.repeat(longestLimit);

    adapter.id = `${input.id}-fabric-adapter`;
    adapter.className = `input-field ${input.dataset.case || 'mixed'}`;
    adapter.hidden = true;
    adapter.dataset.byoPreviewAdapterFor = input.id;
    adapter.dataset.position = input.dataset.side;
    adapter.dataset.canvas = editorId;
    adapter.dataset.lowercaseMax = lowerLimit;
    adapter.dataset.uppercaseMax = upperLimit;
    adapter.dataset.mixedMax = mixedLimit;
    adapter.dataset.color = settings.color || '#000000';
    adapter.dataset.fontFamily = settings.fontFamily || 'Arial';
    adapter.dataset.fabricProps = JSON.stringify(fabricProps);
    adapter.dataset.fontSize = Number(settings.fontSize || 20);
    adapter.dataset.fontSizeOverrideLowercase = settings.lowercaseOverride ? Number(settings.lowercaseOverride) : '';
    adapter.dataset.fontSizeOverrideMixedcase = settings.mixedOverride ? Number(settings.mixedOverride) : '';
    adapter.dataset.fontSizeOverrideUppercase = settings.uppercaseOverride ? Number(settings.uppercaseOverride) : '';
    adapter.dataset.fontWeight = settings.fontWeight || 400;
    adapter.dataset.fontAlign = input.dataset.alignment || settings.alignment || 'center';
    adapter.dataset.fontLetterSpacing = Number(settings.letterSpacing || 0);
    adapter.dataset.placeholder = placeholder;

    if ( input.dataset.byoCircleField === 'true' ) {
      adapter.dataset.radius = input.dataset.circleRadius;
      adapter.dataset.startAngle = input.dataset.circleStartAngle;
      adapter.dataset.wordPos = input.dataset.circleWordPosition;
    }

    this.syncFabricAdapter(input, adapter);

    return adapter;
  },

  // Build the hidden drawer-compatible gemstone selection used by the circle renderer.
  createCircleGemstoneAdapter: function (gemstoneField) {
    const adapter = document.createElement('div');
    const selectedWrapper = document.createElement('div');
    const swatch = document.createElement('span');
    const image = document.createElement('img');
    const positionData = document.createElement('span');

    adapter.className = 'embeded-gemstone-container circle-pendant';
    adapter.dataset.byoCircleGemstoneAdapterFor = gemstoneField.id;
    selectedWrapper.dataset.optionSelected = '';
    swatch.className = 'swatch';
    image.className = 'gemstone-image';
    image.alt = '';
    positionData.dataset.wordPos = gemstoneField.dataset.circleWordPosition;
    positionData.dataset.radius = gemstoneField.dataset.circleRadius;
    positionData.dataset.startAngle = gemstoneField.dataset.circleStartAngle;
    swatch.append(image);
    selectedWrapper.append(swatch);
    adapter.append(selectedWrapper, positionData);
    this.syncCircleGemstoneAdapter(gemstoneField, adapter);

    return adapter;
  },

  // Update a gemstone dropdown with its currently selected swatch and name.
  syncGemstoneDropdown: function (gemstoneField) {
    const selectedButton = gemstoneField.querySelector('[data-personalization-type="gemstone"][aria-pressed="true"]');
    const dropdownToggle = gemstoneField.querySelector('[data-byo-gemstone-dropdown-toggle]');
    const dropdownImage = gemstoneField.querySelector('[data-byo-gemstone-dropdown-image]');
    const dropdownText = gemstoneField.querySelector('[data-byo-gemstone-dropdown-text]');
    const dropdownOptions = gemstoneField.querySelector('.byo-personalization__gemstone-dropdown-options');

    if ( dropdownImage ) {
      dropdownImage.src = selectedButton?.dataset.gemstoneImage || '';
      dropdownImage.hidden = !selectedButton?.dataset.gemstoneImage;
    }

    if ( dropdownText ) {
      dropdownText.textContent = selectedButton?.dataset.personalizationName || 'Select One';
    }

    if ( dropdownToggle ) {
      dropdownToggle.setAttribute('aria-expanded', 'false');
    }

    if ( dropdownOptions ) {
      dropdownOptions.hidden = true;
    }
  },

  // Insert one inline gemstone token into its linked text field and reset the dropdown.
  handleInlineGemstoneChoice: function (personalization, choice) {
    const gemstoneField = choice.closest('[data-byo-inline-gemstone-field]');
    const fieldId = gemstoneField?.dataset.targetField || '';
    const input = fieldId ? personalization.querySelector(`#${CSS.escape(fieldId)}`) : null;
    const dropdownToggle = gemstoneField?.querySelector('[data-byo-gemstone-dropdown-toggle]');
    const dropdownText = gemstoneField?.querySelector('[data-byo-gemstone-dropdown-text]');
    const dropdownOptions = gemstoneField?.querySelector('.byo-personalization__gemstone-dropdown-options');

    if ( !gemstoneField || !input ) {
      return;
    }

    const gemstoneLimit = Number(gemstoneField.dataset.gemstoneLimit || 1);
    const characterLimit = this.getInputCharacterLimit(input);
    const currentGemstones = input.querySelectorAll('[data-byo-inline-gemstone]');
    const hasReachedGemstoneLimit = currentGemstones.length >= gemstoneLimit;
    const characterCount = this.getPersonalizationCharacterCount(input);
    const hasReachedCharacterLimit = characterLimit > 0 && characterCount >= characterLimit;

    if ( !hasReachedGemstoneLimit && !hasReachedCharacterLimit ) {
      const gemstoneImage = document.createElement('img');

      gemstoneImage.src = choice.dataset.gemstoneImage || '';
      gemstoneImage.alt = choice.dataset.personalizationName || choice.dataset.value;
      gemstoneImage.dataset.byoInlineGemstone = '';
      gemstoneImage.dataset.symbolType = 'gemstone';
      gemstoneImage.dataset.characterCount = '1';
      gemstoneImage.contentEditable = 'false';
      this.insertAtInlineEditorCaret(input, gemstoneImage);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if ( dropdownText ) {
      dropdownText.textContent = 'Add a Gemstone';
    }

    if ( dropdownToggle ) {
      dropdownToggle.setAttribute('aria-expanded', 'false');
      dropdownToggle.focus();
    }

    if ( dropdownOptions ) {
      dropdownOptions.hidden = true;
    }
  },

  // Add or replace a standalone gemstone image on its side's Fabric canvas.
  updateStandaloneGemstonePreview: function (personalization, gemstoneField) {
    const state = personalization.byoPreviewState || null;
    const sideName = gemstoneField.dataset.side;
    const sideState = state?.[sideName] || null;
    const selectedButton = gemstoneField.querySelector('[data-personalization-type="gemstone"][aria-pressed="true"]');
    const imageUrl = selectedButton?.dataset.gemstoneImage || '';
    let fabricProps = {};

    if ( !sideState || !imageUrl || !window.fabric ) {
      const completedUpdate = Promise.resolve();
      return completedUpdate;
    }

    try {
      fabricProps = JSON.parse(gemstoneField.dataset.byoGemstoneFabricProps || '{}');
    } catch (error) {
      fabricProps = {};
    }

    const previousUpdate = sideState.gemstonePromise || Promise.resolve();
    const gemstoneUpdate = previousUpdate.catch(() => {
      // A failed earlier image load should not prevent a new gemstone selection.
    }).then(async () => {
      await sideState.readyPromise;

      const editor = state.drawerManager.fabricMap[sideState.editorId];

      if ( !editor ) {
        return;
      }

      await new Promise((resolve) => {
        if ( editor.imageLayer ) {
          window.fabric.util.loadImage(imageUrl, (imageElement) => {
            if ( imageElement ) {
              editor.imageLayer.setElement(imageElement);
              editor.imageLayer.dirty = true;
              sideState.canvas.renderAll();
            }

            resolve();
          }, null, 'Anonymous');
        } else {
          window.fabric.Image.fromURL(imageUrl, (image) => {
            if ( image ) {
              image.set({
                ...fabricProps,
                data: { role: 'gemstone' }
              });
              sideState.canvas.add(image);
              editor.imageLayer = image;
              sideState.canvas.renderAll();
            }

            resolve();
          }, { crossOrigin: 'Anonymous' });
        }
      });
    });
    const handledUpdate = gemstoneUpdate.catch((error) => {
      console.error('Unable to update the BYO gemstone preview.', error);
    });

    sideState.gemstonePromise = handledUpdate;
    return handledUpdate;
  },

  // Copy the selected visible circle gemstone image into its hidden renderer adapter.
  syncCircleGemstoneAdapter: function (gemstoneField, adapter) {
    const selectedButton = gemstoneField.querySelector('[data-byo-circle-gemstone-choice][aria-pressed="true"]');
    const image = adapter.querySelector('.gemstone-image');

    if ( image ) {
      image.src = selectedButton?.dataset.gemstoneImage || '';
      image.alt = selectedButton?.dataset.personalizationName || '';
    }

    this.syncGemstoneDropdown(gemstoneField);
  },

  // Copy the visible BYO input value and current controls into its Fabric adapter.
  syncFabricAdapter: function (input, adapter) {
    const personalization = input.closest('[data-byo-personalization]');
    const drawerManager = this.getDrawerManager(personalization);

    const appendText = function (textContent) {
      let normalizedText = textContent || '';

      if ( drawerManager?.normalizeHearts ) {
        normalizedText = drawerManager.normalizeHearts(normalizedText);
      }

      for ( let characterIndex = 0; characterIndex < normalizedText.length; characterIndex++ ) {
        let character = normalizedText[characterIndex];
        const nextCharacter = normalizedText[characterIndex + 1];
        const isHeart = character === '\u2764' || character === '\u2665';
        const hasVariationSelector = nextCharacter === '\uFE0E' || nextCharacter === '\uFE0F';

        if ( isHeart && hasVariationSelector ) {
          character += nextCharacter;
          characterIndex++;
        }

        const characterElement = document.createElement('span');
        characterElement.textContent = character;
        adapter.append(characterElement);
      }
    };

    adapter.classList.remove('lowercase', 'uppercase', 'mixed');
    adapter.classList.add(input.dataset.case || 'mixed');
    adapter.dataset.fontAlign = input.dataset.alignment || adapter.dataset.fontAlign;
    adapter.replaceChildren();

    if ( input.dataset.byoInlineEditor === 'true' ) {
      for ( const childNode of input.childNodes ) {
        if ( childNode.nodeType === Node.ELEMENT_NODE && childNode.nodeName === 'IMG' ) {
          adapter.append(childNode.cloneNode(true));
        } else {
          const textContent = childNode.textContent || '';
          appendText(textContent);
        }
      }
    } else {
      appendText(this.getInputValue(input));
    }
  },

  // Create one Fabric canvas and register it with the selected shared drawer manager.
  createPreviewCanvas: function (personalization, sideName, imageData, drawerManager) {
    const modal = personalization.closest('x-modal');
    const canvasElement = modal?.querySelector(`[data-byo-preview-canvas="${sideName}"]`);

    if ( !canvasElement || !window.fabric || !imageData?.value ) {
      return null;
    }

    const editorId = `${personalization.id}-${sideName}`;
    const canvas = new window.fabric.Canvas(canvasElement, {
      selection: false,
      hoverCursor: 'default'
    });

    canvas.setWidth(600);
    canvas.setHeight(600);
    canvas.lowerCanvasEl.dataset.canvasFor = sideName;
    drawerManager.fabricMap[editorId] = {
      canvas,
      image_url: imageData.value,
      image_fabric_props: imageData.fabric_props || {}
    };

    const imageProps = imageData.fabric_props || {};

    const readyPromise = new Promise((resolve) => {
      window.fabric.Image.fromURL(imageData.value, (image) => {
        if ( !image ) {
          resolve();
          return;
        }

        image.set(imageProps);
        canvas.setBackgroundImage(image, () => {
          canvas.requestRenderAll();
          const previewUpdate = this.updatePreviewSide(personalization, sideName);

          if ( previewUpdate?.finally ) {
            previewUpdate.finally(resolve);
          } else {
            resolve();
          }
        });
      }, { crossOrigin: 'Anonymous' });
    });

    const sideState = {
      canvas,
      editorId,
      readyPromise
    };

    return sideState;
  },

  // Build both available preview canvases from the currently selected option JSON.
  buildPreviews: function (personalization, frontComponents, backComponents) {
    this.disposePreviews(personalization);

    const drawerManager = this.getDrawerManager(personalization);
    const frontImage = this.findPreviewImage(frontComponents);
    const backImage = this.findPreviewImage(backComponents);

    if ( !drawerManager || !window.fabric || (!frontImage && !backImage) ) {
      return;
    }

    this.configureDrawerManager(drawerManager);

    const adapters = document.createElement('div');
    const state = {
      drawerManager,
      front: this.createPreviewCanvas(personalization, 'front', frontImage, drawerManager),
      back: this.createPreviewCanvas(personalization, 'back', backImage, drawerManager)
    };

    adapters.dataset.byoPreviewAdapters = '';
    adapters.hidden = true;
    personalization.append(adapters);
    personalization.byoPreviewState = state;

    const inputs = personalization.querySelectorAll('[data-byo-personalization-input]');
    const circleScopes = {};

    for ( const input of inputs ) {
      const sideState = state[input.dataset.side] || null;

      if ( !sideState ) {
        continue;
      }

      const adapter = this.createFabricAdapter(personalization, input, drawerManager, sideState.editorId);

      if ( input.dataset.byoCircleField === 'true' ) {
        let circleScope = circleScopes[input.dataset.side] || null;

        if ( !circleScope ) {
          circleScope = document.createElement('div');
          circleScope.className = 'circle-pendant-form-wrapper';
          circleScope.dataset.option = input.dataset.side;
          circleScope.dataset.amountOfFields = input.dataset.circleFieldCount;
          circleScopes[input.dataset.side] = circleScope;
          adapters.append(circleScope);
        }

        circleScope.append(adapter);
      } else {
        adapters.append(adapter);
      }
    }

    const circleGemstoneFields = personalization.querySelectorAll('[data-byo-circle-gemstone]');

    for ( const gemstoneField of circleGemstoneFields ) {
      const sideName = gemstoneField.dataset.side;
      const sideState = state[sideName] || null;
      let circleScope = circleScopes[sideName] || null;

      if ( !sideState ) {
        continue;
      }

      if ( !circleScope ) {
        circleScope = document.createElement('div');
        circleScope.className = 'circle-pendant-form-wrapper';
        circleScope.dataset.option = sideName;
        circleScope.dataset.amountOfFields = gemstoneField.dataset.circleFieldCount;
        circleScopes[sideName] = circleScope;
        adapters.append(circleScope);
      }

      circleScope.append(this.createCircleGemstoneAdapter(gemstoneField));
    }

    this.setPreviewAvailability(personalization, Boolean(state.front), Boolean(state.back));
    this.showPreview(personalization, 'product');
    this.updatePreviewSide(personalization, 'front');
    this.updatePreviewSide(personalization, 'back');
  },

  // Ask the existing drawer engine to redraw all fields associated with one side.
  updatePreviewSide: function (personalization, sideName) {
    const state = personalization.byoPreviewState || null;
    const sideState = state?.[sideName] || null;

    if ( !state?.drawerManager || !sideState ) {
      const completedUpdate = Promise.resolve();
      return completedUpdate;
    }

    const adapters = personalization.querySelectorAll(`[data-byo-preview-adapter-for][data-position="${sideName}"]`);
    const previousUpdate = sideState.updatePromise || Promise.resolve();
    const previewUpdate = previousUpdate.catch(() => {
      // A failed earlier render should not prevent the next requested render.
    }).then(() => {
      const currentInputs = personalization.querySelectorAll(`[data-byo-personalization-input][data-side="${sideName}"]`);

      for ( const currentInput of currentInputs ) {
        const adapterSelector = `[data-byo-preview-adapter-for="${CSS.escape(currentInput.id)}"]`;
        const adapter = personalization.querySelector(adapterSelector);

        if ( adapter ) {
          this.syncFabricAdapter(currentInput, adapter);
        }
      }

      const currentGemstoneFields = personalization.querySelectorAll(`[data-byo-circle-gemstone][data-side="${sideName}"]`);

      for ( const currentGemstoneField of currentGemstoneFields ) {
        const adapterSelector = `[data-byo-circle-gemstone-adapter-for="${CSS.escape(currentGemstoneField.id)}"]`;
        const adapter = personalization.querySelector(adapterSelector);

        if ( adapter ) {
          this.syncCircleGemstoneAdapter(currentGemstoneField, adapter);
        }
      }

      const drawerUpdate = state.drawerManager.updateFabricCanvas(adapters, sideState.editorId);
      return drawerUpdate;
    });
    const handledUpdate = previewUpdate.catch((error) => {
      console.error('Unable to update the BYO personalization preview.', error);
    });

    sideState.updatePromise = handledUpdate;
    return handledUpdate;
  },

  // Create Shopify-uploadable preview files without placing large image data in local storage.
  getPreviewFiles: async function (personalization) {
    const state = personalization.byoPreviewState || null;
    const previewFiles = {};

    if ( !state?.drawerManager ) {
      return previewFiles;
    }

    const backInputs = personalization.querySelectorAll('[data-byo-personalization-input][data-side="back"]');
    let hasBackPersonalization = false;

    for ( const backInput of backInputs ) {
      if ( this.getInputValue(backInput).trim() ) {
        hasBackPersonalization = true;
        break;
      }
    }

    for ( const sideName of ['front', 'back'] ) {
      const sideState = state[sideName] || null;

      if ( !sideState || (sideName === 'back' && !hasBackPersonalization) ) {
        continue;
      }

      await sideState.readyPromise;

      if ( sideState.updatePromise ) {
        await sideState.updatePromise;
      }

      if ( sideState.gemstonePromise ) {
        await sideState.gemstonePromise;
      }

      sideState.canvas.renderAll();

      const imageData = sideState.canvas.toDataURL('image/png');
      const imageBlob = state.drawerManager.base64ToBlob(imageData);
      previewFiles[`custom_preview_${sideName}`] = imageBlob;
    }

    return previewFiles;
  },

  // Synchronize one input and display its corresponding live preview.
  updatePreviewForInput: function (personalization, input, showSide = true) {
    const adapter = personalization.querySelector(`[data-byo-preview-adapter-for="${CSS.escape(input.id)}"]`);

    if ( !adapter ) {
      return;
    }

    this.syncFabricAdapter(input, adapter);
    this.updatePreviewSide(personalization, input.dataset.side);

    if ( showSide ) {
      this.showPreview(personalization, input.dataset.side);
    }
  },

  // Choose a character limit based on the active text-case choice.
  getCharacterLimit: function (fontSettings, activeCase) {
    if ( activeCase === 'lowercase' ) {
      const lowercaseLimit = Number(fontSettings?.lowercase_text_limit?.value || 0);
      return lowercaseLimit;
    }

    if ( activeCase === 'uppercase' ) {
      const uppercaseLimit = Number(fontSettings?.uppercase_text_limit?.value || 0);
      return uppercaseLimit;
    }

    const mixedCaseLimit = Number(fontSettings?.mixed_case_text_limit?.value || 0);
    return mixedCaseLimit;
  },

  // Apply text case without removing inline gemstone image nodes.
  applyInputCase: function (input, textCase) {
    if ( input.dataset.byoInlineEditor === 'true' ) {
      const selection = window.getSelection();
      let startContainer = null;
      let startOffset = 0;
      let endContainer = null;
      let endOffset = 0;
      let hasEditorSelection = false;

      if ( selection && selection.rangeCount > 0 ) {
        const activeRange = selection.getRangeAt(0);

        if ( input.contains(activeRange.commonAncestorContainer) ) {
          startContainer = activeRange.startContainer;
          startOffset = activeRange.startOffset;
          endContainer = activeRange.endContainer;
          endOffset = activeRange.endOffset;
          hasEditorSelection = true;
        }
      }

      const textWalker = document.createTreeWalker(input, NodeFilter.SHOW_TEXT);
      let textNode = textWalker.nextNode();
      let textChanged = false;

      while ( textNode ) {
        let updatedText = textNode.nodeValue;

        if ( textCase === 'uppercase' ) {
          updatedText = textNode.nodeValue.toUpperCase();
        } else if ( textCase === 'lowercase' ) {
          updatedText = textNode.nodeValue.toLowerCase();
        }

        if ( updatedText !== textNode.nodeValue ) {
          textNode.nodeValue = updatedText;
          textChanged = true;
        }

        textNode = textWalker.nextNode();
      }

      if ( textChanged && hasEditorSelection && input.contains(startContainer) && input.contains(endContainer) ) {
        const restoredRange = document.createRange();
        const restoredStartOffset = Math.min(startOffset, startContainer.length ?? startContainer.childNodes.length);
        const restoredEndOffset = Math.min(endOffset, endContainer.length ?? endContainer.childNodes.length);

        restoredRange.setStart(startContainer, restoredStartOffset);
        restoredRange.setEnd(endContainer, restoredEndOffset);
        selection.removeAllRanges();
        selection.addRange(restoredRange);
        this.inlineSelectionState.set(input, restoredRange.cloneRange());
      }
    } else if ( textCase === 'uppercase' ) {
      const uppercaseValue = this.getInputValue(input).toUpperCase();
      this.setInputValue(input, uppercaseValue);
    } else if ( textCase === 'lowercase' ) {
      const lowercaseValue = this.getInputValue(input).toLowerCase();
      this.setInputValue(input, lowercaseValue);
    }
  },

  // Remember the caret position so a dropdown selection inserts at that exact point.
  saveInlineEditorSelection: function (input) {
    const selection = window.getSelection();

    if ( !selection || selection.rangeCount === 0 ) {
      return;
    }

    const currentRange = selection.getRangeAt(0);

    if ( input.contains(currentRange.commonAncestorContainer) ) {
      this.inlineSelectionState.set(input, currentRange.cloneRange());
    }
  },

  // Insert a text or image node at the saved editor caret and move the caret after it.
  insertAtInlineEditorCaret: function (input, node) {
    const savedRange = this.inlineSelectionState.get(input) || null;

    if ( savedRange && input.contains(savedRange.commonAncestorContainer) ) {
      savedRange.deleteContents();
      savedRange.insertNode(node);
    } else {
      input.append(node);
    }

    const nextRange = document.createRange();
    nextRange.setStartAfter(node);
    nextRange.collapse(true);
    this.inlineSelectionState.set(input, nextRange.cloneRange());

    const selection = window.getSelection();

    if ( selection ) {
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }
  },

  // Normalize a symbol so its text-presentation marker does not create a second lookup key.
  getCharacterCountKey: function (value) {
    const characterKey = String(value || '').replace(/[\uFE0E\uFE0F]/g, '');
    return characterKey;
  },

  // Build the same symbol-to-character-space lookup used by the personalization drawer.
  getSymbolCharacterCounts: function (input) {
    const personalization = input.closest('[data-byo-personalization]');
    const symbolButtons = personalization?.querySelectorAll('[data-personalization-type="symbol"]') || [];
    const characterCounts = {};

    for ( const symbolButton of symbolButtons ) {
      const characterCount = Number(symbolButton.dataset.characterCount || 1);
      const symbolKey = this.getCharacterCountKey(symbolButton.dataset.symbol);
      const imageKey = symbolButton.dataset.symbolImage || '';

      if ( symbolKey ) {
        characterCounts[symbolKey] = characterCount;
      }

      if ( imageKey ) {
        characterCounts[imageKey] = characterCount;
      }
    }

    return characterCounts;
  },

  // Count visible text and inline items using their JSON character-space values.
  getPersonalizationCharacterCount: function (input) {
    const inputValue = this.getInputValue(input);
    const characterCounts = this.getSymbolCharacterCounts(input);
    let characterCount = 0;

    for ( const character of inputValue ) {
      if ( character !== '\uFE0E' && character !== '\uFE0F' ) {
        const characterKey = this.getCharacterCountKey(character);
        characterCount += Number(characterCounts[characterKey] || 1);
      }
    }

    if ( input.dataset.byoInlineEditor === 'true' ) {
      const inlineImages = input.querySelectorAll('[data-byo-inline-gemstone], [data-byo-inline-symbol]');

      for ( const inlineImage of inlineImages ) {
        const imageSource = inlineImage.getAttribute('src') || '';
        const savedCharacterCount = Number(inlineImage.dataset.characterCount || 0);
        const imageCharacterCount = savedCharacterCount || Number(characterCounts[imageSource] || 1);
        characterCount += imageCharacterCount;
      }
    }

    return characterCount;
  },

  // Return the configured maximum for the field's currently selected case.
  getInputCharacterLimit: function (input) {
    let fabricSettings = {};

    try {
      fabricSettings = JSON.parse(input.dataset.byoFabricSettings || '{}');
    } catch (error) {
      fabricSettings = {};
    }

    const activeCase = input.dataset.case;
    let characterLimit = Number(fabricSettings.mixedLimit || 0);

    if ( activeCase === 'lowercase' ) {
      characterLimit = Number(fabricSettings.lowerLimit || 0);
    } else if ( activeCase === 'uppercase' ) {
      characterLimit = Number(fabricSettings.upperLimit || 0);
    }

    return characterLimit;
  },

  // Trim a text value using the JSON weight assigned to each available symbol.
  trimTextToCharacterLimit: function (input, textValue, characterLimit) {
    const characterCounts = this.getSymbolCharacterCounts(input);
    let trimmedText = '';
    let currentCount = 0;

    for ( const character of textValue ) {
      if ( character === '\uFE0E' || character === '\uFE0F' ) {
        trimmedText += character;
        continue;
      }

      const characterKey = this.getCharacterCountKey(character);
      const characterCount = Number(characterCounts[characterKey] || 1);

      if ( currentCount + characterCount > characterLimit ) {
        break;
      }

      trimmedText += character;
      currentCount += characterCount;
    }

    return trimmedText;
  },

  // Remove content beyond an editable div's active character limit.
  trimInlineEditorToLimit: function (input, characterLimit) {
    const currentCount = this.getPersonalizationCharacterCount(input);
    const characterCounts = this.getSymbolCharacterCounts(input);

    if ( characterLimit <= 0 || currentCount <= characterLimit ) {
      return;
    }

    const trimmedContent = document.createDocumentFragment();
    let remainingCharacters = characterLimit;

    for ( const childNode of input.childNodes ) {
      if ( remainingCharacters <= 0 ) {
        break;
      }

      if ( childNode.nodeType === Node.ELEMENT_NODE && childNode.nodeName === 'IMG' ) {
        const imageSource = childNode.getAttribute('src') || '';
        const savedCharacterCount = Number(childNode.dataset.characterCount || 0);
        const imageCharacterCount = savedCharacterCount || Number(characterCounts[imageSource] || 1);

        if ( imageCharacterCount > remainingCharacters ) {
          break;
        }

        trimmedContent.append(childNode.cloneNode(true));
        remainingCharacters -= imageCharacterCount;
        continue;
      }

      const nodeText = childNode.textContent || '';
      let trimmedText = '';

      for ( const character of nodeText ) {
        if ( character === '\uFE0E' || character === '\uFE0F' ) {
          trimmedText += character;
          continue;
        }

        if ( remainingCharacters <= 0 ) {
          break;
        }

        const characterKey = this.getCharacterCountKey(character);
        const characterCount = Number(characterCounts[characterKey] || 1);

        if ( characterCount > remainingCharacters ) {
          remainingCharacters = 0;
          break;
        }

        trimmedText += character;
        remainingCharacters -= characterCount;
      }

      if ( trimmedText ) {
        trimmedContent.append(document.createTextNode(trimmedText));
      }
    }

    input.replaceChildren(trimmedContent);

    const selectionRange = document.createRange();
    selectionRange.selectNodeContents(input);
    selectionRange.collapse(false);
    this.inlineSelectionState.set(input, selectionRange.cloneRange());

    const selection = window.getSelection();

    if ( selection ) {
      selection.removeAllRanges();
      selection.addRange(selectionRange);
    }
  },

  // Apply the active case's character limit and update the visible field counter.
  updateCharacterLimit: function (personalization, input) {
    const limit = this.getInputCharacterLimit(input);
    input.maxLength = limit > 0 ? limit : 1000;

    let inputValue = this.getInputValue(input);

    if ( input.dataset.byoInlineEditor === 'true' && !this.composingInputs.has(input) ) {
      this.trimInlineEditorToLimit(input, limit);
      inputValue = this.getInputValue(input);
    } else if ( input.dataset.byoInlineEditor !== 'true' && limit > 0 ) {
      const trimmedInputValue = this.trimTextToCharacterLimit(input, inputValue, limit);

      if ( trimmedInputValue !== inputValue ) {
        inputValue = trimmedInputValue;
        this.setInputValue(input, inputValue);
      }
    }

    const characterCount = this.getPersonalizationCharacterCount(input);

    const counter = input.parentElement.querySelector('[data-byo-personalization-counter]');

    if ( !counter ) {
      return
    }

    if ( limit <= 0 ) {
      counter.textContent = '';
    } else if ( characterCount > 0 ) {
      counter.textContent = `${characterCount}/${limit}`;
    } else {
      counter.textContent = `${limit} ${personalization.dataset.characterLimitLabel}`;
    }

  },

  // Load the large personalization metafield only when its product modal is opened.
  loadForm: async function (personalization) {
    if ( personalization.dataset.byoPersonalizationLoaded === 'true' ) {
      this.restoreSavedForm(personalization);
      return
    }

    if ( personalization.dataset.byoPersonalizationLoading === 'true' ) {
      return
    }

    const personalizationUrl = personalization.dataset.byoPersonalizationUrl;

    if ( !personalizationUrl ) {
      return
    }

    personalization.dataset.byoPersonalizationLoading = 'true';

    try {
      const response = await fetch(personalizationUrl);

      if ( !response.ok ) {
        throw new Error(`Unable to load personalization form: ${response.status}`);
      }

      const responseHtml = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
      const personalizationContent = responseDocument.querySelector('[data-byo-personalization-content]');

      if ( !personalizationContent ) {
        throw new Error('Personalization form content was not found.');
      }

      personalization.dataset.backLabel = personalizationContent.dataset.byoBackLabel || personalization.dataset.backLabel;
      personalization.replaceChildren(...personalizationContent.childNodes);
      personalization.dataset.byoPersonalizationLoaded = 'true';
      this.buildForm(personalization);
      this.restoreSavedForm(personalization);
    } catch (error) {
      const errorMessage = document.createElement('p');
      errorMessage.className = 'byo-personalization__message';
      errorMessage.textContent = 'Personalization options could not be loaded.';
      personalization.replaceChildren(errorMessage);
      console.error(error);
    } finally {
      personalization.removeAttribute('data-byo-personalization-loading');
    }
  },

  // Create a selectable icon-style button used by case, alignment, symbol, and gemstone controls.
  createChoiceButton: function (label, group, value, content = '') {
    const button = document.createElement('button');
    const visibleContent = content || label;

    button.type = 'button';
    button.className = 'byo-personalization__choice';

    if ( group.startsWith('case-') ) {
      button.classList.add('text-case-align');
    } else if ( group.startsWith('alignment-') ) {
      button.classList.add('alignment-button');
    } else if ( group.startsWith('symbol-') ) {
      button.classList.add('symbol-button');
    } else {
      button.classList.add('gemstone-button');
    }

    button.dataset.byoPersonalizationChoice = group;
    button.dataset.value = value;
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = visibleContent;

    return button;
  },

  // Build a row of case or alignment buttons from the metafield settings.
  buildOptionButtons: function (wrapper, labelText, group, selectedValues, optionDefinitions) {
    if ( selectedValues.length < 2 ) {
      return
    }

    const row = document.createElement('div');
    const label = document.createElement('span');

    row.className = 'byo-personalization__choice-row';
    label.className = 'byo-personalization__choice-label';
    label.textContent = labelText ? `${labelText.replace(/:$/, '')}:` : '';
    row.append(label);

    for ( let i = 0; i < selectedValues.length; i += 1 ) {
      const value = selectedValues[i];
      const option = this.findOption(optionDefinitions, value);
      const button = this.createChoiceButton(option?.value || value, group, value, option?.icon || '');

      if ( i === 0 ) {
        button.classList.add('is-selected', 'enabled-box');
        button.setAttribute('aria-pressed', 'true');
      }

      row.append(button);
    }

    wrapper.append(row);
  },

  // Build symbol and gemstone swatches linked to a text field.
  buildDecorations: function (wrapper, component, fieldId, sortOrder, personalization) {
    const fieldOptions = component?.field_options || {};
    const iconSetting = fieldOptions?.icon_settings?.options?.icons;
    const gemstoneSetting = fieldOptions?.gemstone_settings?.options?.gemstones;
    const groups = [
      {
        setting: iconSetting,
        order: sortOrder.icons,
        label: personalization.dataset.symbolLabel,
        group: `symbol-${fieldId}`,
        dataKey: 'linked_symbol_data'
      },
      {
        setting: gemstoneSetting,
        order: sortOrder.gemstones,
        label: personalization.dataset.gemstoneLabel,
        group: `gemstone-${fieldId}`,
        dataKey: 'linked_gemstone_data'
      }
    ];

    for ( const group of groups ) {
      const values = this.getSelectedValues(group.setting?.value, group.order);

      if ( values.length === 0 ) {
        continue
      }

      if ( group.group.startsWith('gemstone-') ) {
        const gemstoneField = document.createElement('div');
        const dropdown = document.createElement('div');
        const dropdownButton = document.createElement('button');
        const selectedText = document.createElement('span');
        const options = document.createElement('div');
        const gemstoneLimit = Number(fieldOptions?.font_settings?.options?.gemstone_limit?.value || 1);

        gemstoneField.className = 'byo-personalization__inline-gemstone-field';
        gemstoneField.dataset.byoInlineGemstoneField = '';
        gemstoneField.dataset.targetField = fieldId;
        gemstoneField.dataset.gemstoneLimit = gemstoneLimit;
        dropdown.className = 'byo-personalization__gemstone-dropdown';
        dropdownButton.type = 'button';
        dropdownButton.className = 'byo-personalization__gemstone-dropdown-toggle';
        dropdownButton.dataset.byoGemstoneDropdownToggle = '';
        dropdownButton.setAttribute('aria-haspopup', 'listbox');
        dropdownButton.setAttribute('aria-expanded', 'false');
        selectedText.dataset.byoGemstoneDropdownText = '';
        selectedText.textContent = 'Add a Gemstone';
        options.className = 'byo-personalization__gemstone-dropdown-options';
        options.id = `${fieldId}-inline-gemstone-options`;
        options.hidden = true;
        options.setAttribute('role', 'listbox');
        dropdownButton.setAttribute('aria-controls', options.id);
        dropdownButton.append(selectedText);
        dropdown.append(dropdownButton, options);

        for ( const value of values ) {
          const option = this.findOption(group.setting?.options, value);
          const linkedData = option?.linked_gemstone_data || {};
          const imageUrl = linkedData.gemstone_image_url || '';
          const name = linkedData.gemstone_name || option?.value || value;
          const content = imageUrl ? `<img src="${imageUrl}" alt=""><span>${name}</span>` : name;
          const button = this.createChoiceButton(name, `inline-gemstone-${fieldId}`, value, content);

          button.dataset.byoInlineGemstoneChoice = '';
          button.dataset.personalizationName = name;
          button.dataset.gemstoneImage = imageUrl;
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', 'false');
          options.append(button);
        }

        gemstoneField.append(dropdown);
        wrapper.append(gemstoneField);
        continue;
      }

      const row = document.createElement('div');
      const label = document.createElement('span');

      row.className = 'byo-personalization__choice-row';

      if ( group.group.startsWith('symbol-') ) {
        row.classList.add('symbol-container');
      }
      label.className = 'byo-personalization__choice-label';
      label.textContent = group.label;
      row.append(label);

      for ( const value of values ) {
        const option = this.findOption(group.setting?.options, value);
        const linkedData = option?.[group.dataKey] || {};
        const imageUrl = linkedData.symbol_image_url || linkedData.gemstone_image_url;
        const symbol = this.normalizeTextHearts(linkedData.symbol_string || option?.icon || '');
        const name = linkedData.symbol_name || linkedData.gemstone_name || option?.value || value;
        let content = symbol || name;

        if ( imageUrl ) {
          content = `<img src="${imageUrl}" alt="">`;
        }

        const button = this.createChoiceButton(name, group.group, value, content);
        const characterCount = Number(linkedData.character_spacing || 1);
        button.dataset.targetField = fieldId;
        button.dataset.symbol = this.normalizeTextHearts(linkedData.symbol_string || '');
        button.dataset.symbolImage = linkedData.symbol_image_url || '';
        button.dataset.characterCount = characterCount;
        button.dataset.personalizationType = group.group.startsWith('gemstone-') ? 'gemstone' : 'symbol';
        button.dataset.personalizationName = name;
        row.append(button);
      }

      wrapper.append(row);
    }
  },

  // Build one normal text personalization field and its related controls.
  buildTextField: function (component, sideName, componentIndex, sortOrder, personalization) {
    const fieldOptions = component?.field_options || {};
    const nameSettings = fieldOptions?.field_name_settings?.options || {};
    const fontSettings = fieldOptions?.font_settings?.options || {};
    const caseSetting = fontSettings.font_case || {};
    const alignmentSetting = fontSettings.alignment || {};
    const cases = this.getSelectedValues(caseSetting.value, sortOrder.font_case);
    const alignments = this.getSelectedValues(alignmentSetting.value, sortOrder.font_alignment);
    const inlineGemstoneSetting = fieldOptions?.gemstone_settings?.options?.gemstones;
    const inlineGemstoneValues = this.getSelectedValues(inlineGemstoneSetting?.value, sortOrder.gemstones);
    const symbolSetting = fieldOptions?.icon_settings?.options?.icons;
    const symbolValues = this.getSelectedValues(symbolSetting?.value, sortOrder.icons);
    let hasInlineImageSymbols = false;

    for ( const symbolValue of symbolValues ) {
      const symbolOption = this.findOption(symbolSetting?.options, symbolValue);

      if ( symbolOption?.linked_symbol_data?.symbol_image_url ) {
        hasInlineImageSymbols = true;
        break;
      }
    }

    const activeCase = cases[0] || 'mixed case';
    const limit = this.getCharacterLimit(fontSettings, activeCase.replace(' case', ''));
    const fieldId = `${personalization.id}-${sideName}-${componentIndex}`;
    const wrapper = document.createElement('div');
    const textOptions = document.createElement('div');
    const labelAndCase = document.createElement('div');
    const label = document.createElement('label');
    const inputWrapper = document.createElement('div');
    const hasInlineContentEditor = !component.byoCircleSettings && (inlineGemstoneValues.length > 0 || hasInlineImageSymbols);
    const input = document.createElement(hasInlineContentEditor ? 'div' : 'input');
    const counter = document.createElement('span');
    const circleSettings = component.byoCircleSettings || null;

    wrapper.className = 'byo-personalization__field';
    textOptions.className = 'byo-personalization__text-options text-options';
    labelAndCase.className = 'byo-personalization__label-and-case label-and-case-wrapper';
    label.className = 'byo-personalization__field-label';
    label.htmlFor = fieldId;
    label.id = `${fieldId}-label`;
    const fieldLabel = nameSettings?.title?.value || 'Text';
    label.textContent = `${fieldLabel.replace(/:$/, '')}:`;
    inputWrapper.className = 'byo-personalization__input-wrapper';
    input.id = fieldId;
    input.className = 'byo-personalization__input';
    input.dataset.byoPersonalizationInput = '';
    input.dataset.fieldLabel = label.textContent;
    input.dataset.side = sideName;
    input.dataset.case = activeCase.replace(' case', '');
    input.dataset.caseOptionCount = cases.length;
    input.dataset.alignment = alignments[0] || '';
    input.dataset.alignmentOptionCount = alignments.length;
    input.maxLength = limit > 0 ? limit : 1000;
    this.updateAutocapitalize(input);

    if ( hasInlineContentEditor ) {
      input.contentEditable = 'true';
      input.dataset.byoInlineEditor = 'true';
      input.setAttribute('role', 'textbox');
      input.setAttribute('aria-labelledby', label.id);
      input.spellcheck = false;
    } else {
      input.type = 'text';
      input.placeholder = '';
    }

    if ( circleSettings ) {
      input.dataset.byoCircleField = 'true';
      input.dataset.circleWordPosition = circleSettings.wordPosition;
      input.dataset.circleRadius = circleSettings.radius;
      input.dataset.circleStartAngle = circleSettings.startAngle;
      input.dataset.circleFieldCount = circleSettings.fieldCount;
    }

    input.dataset.byoFabricSettings = JSON.stringify({
      fabricProps: circleSettings?.fabricProps || nameSettings?.body?.fabric_props || {},
      placeholder: nameSettings?.body?.value || '',
      color: fontSettings?.color?.value || '#000000',
      fontFamily: fontSettings?.font?.value || 'Arial',
      fontSize: fontSettings?.font_size?.value || 20,
      lowercaseOverride: fontSettings?.lowercase_text_override?.value || '',
      mixedOverride: fontSettings?.mixed_case_text_font_size_override?.value || '',
      uppercaseOverride: fontSettings?.uppercase_text_font_size_override?.value || '',
      fontWeight: fontSettings?.font_weight?.value || 400,
      letterSpacing: circleSettings?.letterSpacing ?? fontSettings?.letter_spacing?.value ?? 0,
      alignment: alignments[0] || 'center',
      lowerLimit: fontSettings?.lowercase_text_limit?.value || 0,
      upperLimit: fontSettings?.uppercase_text_limit?.value || 0,
      mixedLimit: fontSettings?.mixed_case_text_limit?.value || 0
    });
    counter.className = 'byo-personalization__counter';
    counter.dataset.byoPersonalizationCounter = '';
    counter.textContent = limit > 0 ? `${limit} ${personalization.dataset.characterLimitLabel}` : '';

    labelAndCase.append(label);
    this.buildOptionButtons(labelAndCase, '', `case-${fieldId}`, cases, caseSetting.options);
    textOptions.append(labelAndCase);
    this.buildOptionButtons(textOptions, personalization.dataset.alignmentLabel, `alignment-${fieldId}`, alignments, alignmentSetting.options);
    wrapper.append(textOptions);
    inputWrapper.append(input, counter);
    wrapper.append(inputWrapper);

    if ( !circleSettings ) {
      this.buildDecorations(wrapper, component, fieldId, sortOrder, personalization);
    }

    const drawerManager = this.getDrawerManager(personalization);
    const fontOptions = fontSettings?.font?.options || [];
    const selectedFont = String(fontSettings?.font?.value || '').trim().toLowerCase();

    if ( drawerManager?.loadFont ) {
      for ( const fontOption of fontOptions ) {
        const fontValue = String(fontOption?.value || '').trim().toLowerCase();

        if ( fontValue === selectedFont && fontOption?.linked_font_data ) {
          drawerManager.loadFont(fontOption.linked_font_data);
          break;
        }
      }
    }

    return wrapper;
  },

  // Build one numbered gemstone selector used as a word on a circle pendant.
  buildCircleGemstone: function (component, sideName, componentIndex, wordPosition, fieldCount, sortOrder, personalization) {
    const fieldOptions = component?.field_options || {};
    const fontSettings = fieldOptions?.font_settings?.options || {};
    const gemstoneSetting = fieldOptions?.gemstone_settings?.options?.gemstones;
    const values = this.getSelectedValues(gemstoneSetting?.value, sortOrder.gemstones);
    const wrapper = document.createElement('fieldset');
    const legend = document.createElement('legend');
    const dropdown = document.createElement('div');
    const dropdownButton = document.createElement('button');
    const selectedImage = document.createElement('img');
    const selectedText = document.createElement('span');
    const row = document.createElement('div');

    wrapper.id = `${personalization.id}-${sideName}-${componentIndex}-gemstone-${wordPosition}`;
    wrapper.className = 'byo-personalization__field';
    wrapper.dataset.byoCircleGemstone = '';
    wrapper.dataset.side = sideName;
    wrapper.dataset.circleWordPosition = wordPosition;
    wrapper.dataset.circleRadius = fontSettings?.radius?.value || 0;
    wrapper.dataset.circleStartAngle = fontSettings?.start_angle?.value || 0;
    wrapper.dataset.circleFieldCount = fieldCount;
    legend.className = 'byo-personalization__field-label';
    const gemstoneLabel = personalization.dataset.circleGemstoneLabel || 'Gemstone {{number}}';
    legend.textContent = `${gemstoneLabel.replace('{{number}}', wordPosition + 1)}:`;
    dropdown.className = 'byo-personalization__gemstone-dropdown';
    dropdownButton.type = 'button';
    dropdownButton.className = 'byo-personalization__gemstone-dropdown-toggle';
    dropdownButton.dataset.byoGemstoneDropdownToggle = '';
    dropdownButton.setAttribute('aria-haspopup', 'listbox');
    dropdownButton.setAttribute('aria-expanded', 'false');
    selectedImage.alt = '';
    selectedImage.hidden = true;
    selectedImage.dataset.byoGemstoneDropdownImage = '';
    selectedText.dataset.byoGemstoneDropdownText = '';
    selectedText.textContent = 'Select One';
    row.className = 'byo-personalization__gemstone-dropdown-options';
    row.id = `${wrapper.id}-options`;
    row.hidden = true;
    row.setAttribute('role', 'listbox');
    dropdownButton.setAttribute('aria-controls', row.id);
    dropdownButton.append(selectedImage, selectedText);
    dropdown.append(dropdownButton, row);
    wrapper.append(legend);

    for ( let i = 0; i < values.length; i += 1 ) {
      const value = values[i];
      const option = this.findOption(gemstoneSetting?.options, value);
      const linkedData = option?.linked_gemstone_data || {};
      const name = linkedData.gemstone_name || option?.value || value;
      const imageUrl = linkedData.gemstone_image_url || '';
      const content = imageUrl ? `<img src="${imageUrl}" alt=""><span>${name}</span>` : name;
      const button = this.createChoiceButton(name, `circle-gemstone-${wrapper.id}`, value, content);

      button.dataset.byoCircleGemstoneChoice = '';
      button.dataset.personalizationType = 'gemstone';
      button.dataset.personalizationName = name;
      button.dataset.gemstoneImage = imageUrl;
      button.setAttribute('role', 'option');

      if ( i === 0 ) {
        button.classList.add('is-selected', 'enabled-box');
        button.setAttribute('aria-pressed', 'true');
        button.setAttribute('aria-selected', 'true');
        selectedImage.src = imageUrl;
        selectedImage.hidden = !imageUrl;
        selectedText.textContent = name;
      } else {
        button.setAttribute('aria-selected', 'false');
      }

      row.append(button);
    }

    wrapper.append(dropdown);
    return wrapper;
  },

  // Build a standalone gemstone component from the metafield settings.
  buildGemstones: function (component, sideName, componentIndex, sortOrder, personalization) {
    const setting = component?.field_options?.gemstone_settings?.options?.gemstones;
    const values = this.getSelectedValues(setting?.value, sortOrder.gemstones);
    const wrapper = document.createElement('fieldset');
    const legend = document.createElement('legend');
    const dropdown = document.createElement('div');
    const dropdownButton = document.createElement('button');
    const selectedImage = document.createElement('img');
    const selectedText = document.createElement('span');
    const row = document.createElement('div');

    wrapper.id = `${personalization.id}-${sideName}-${componentIndex}-gemstones`;
    wrapper.className = 'byo-personalization__field';
    wrapper.dataset.byoGemstoneField = '';
    wrapper.dataset.side = sideName;
    wrapper.dataset.byoGemstoneFabricProps = JSON.stringify(component?.field_options?.upload_image?.options?.image?.fabric_props || {});
    legend.className = 'byo-personalization__field-label';
    legend.textContent = personalization.dataset.gemstoneLabel;
    wrapper.append(legend);

    dropdown.className = 'byo-personalization__gemstone-dropdown';
    dropdownButton.type = 'button';
    dropdownButton.className = 'byo-personalization__gemstone-dropdown-toggle';
    dropdownButton.dataset.byoGemstoneDropdownToggle = '';
    dropdownButton.setAttribute('aria-haspopup', 'listbox');
    dropdownButton.setAttribute('aria-expanded', 'false');
    selectedImage.alt = '';
    selectedImage.hidden = true;
    selectedImage.dataset.byoGemstoneDropdownImage = '';
    selectedText.dataset.byoGemstoneDropdownText = '';
    selectedText.textContent = 'Select One';
    row.className = 'byo-personalization__gemstone-dropdown-options';
    row.id = `${wrapper.id}-options`;
    row.hidden = true;
    row.setAttribute('role', 'listbox');
    dropdownButton.setAttribute('aria-controls', row.id);
    dropdownButton.append(selectedImage, selectedText);
    dropdown.append(dropdownButton, row);

    for ( const value of values ) {
      const option = this.findOption(setting?.options, value);
      const linkedData = option?.linked_gemstone_data || {};
      const name = linkedData.gemstone_name || option?.value || value;
      const imageUrl = linkedData.gemstone_image_url || '';
      const content = imageUrl ? `<img src="${imageUrl}" alt=""><span>${name}</span>` : name;
      const button = this.createChoiceButton(name, `gemstones-${sideName}-${componentIndex}`, value, content);
      button.dataset.personalizationType = 'gemstone';
      button.dataset.personalizationName = name;
      button.dataset.gemstoneImage = imageUrl;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      row.append(button);
    }

    wrapper.append(dropdown);
    return wrapper;
  },

  // Build the repeating text/gemstone fields used by circle-text products.
  buildCircleText: function (component, sideName, componentIndex, sortOrder, personalization) {
    const fragment = document.createDocumentFragment();
    const fontSettings = component?.field_options?.font_settings?.options || {};
    const fieldCount = Number(fontSettings?.word_count?.value || 0);
    const drawerManager = this.getDrawerManager(personalization);
    const letterSpacingValue = Number(fontSettings?.letter_spacing?.value || 0);
    const fontSize = Number(fontSettings?.font_size?.value || 1);
    let circleLetterSpacing = letterSpacingValue;

    if ( drawerManager?.characterSpacingFormula ) {
      circleLetterSpacing = drawerManager.characterSpacingFormula(letterSpacingValue, fontSize);
    }

    for ( let i = 0; i < fieldCount; i += 1 ) {
      const fieldValue = fontSettings[`word_${i + 1}`]?.value;

      if ( fieldValue === '{{GEMSTONE}}' ) {
        fragment.append(this.buildCircleGemstone(component, sideName, componentIndex, i, fieldCount, sortOrder, personalization));
      } else {
        const clonedComponent = {
          field_options: {
            ...component.field_options,
            field_name_settings: {
              options: {
                field_name_settings: {},
                title: {},
                body: {}
              }
            }
          }
        };
        const textLabel = personalization.dataset.circleTextLabel || 'Word {{number}}';
        clonedComponent.field_options.field_name_settings.options.title.value = textLabel.replace('{{number}}', i + 1);
        clonedComponent.field_options.field_name_settings.options.body.value = fieldValue || '';
        clonedComponent.byoCircleSettings = {
          wordPosition: i,
          fieldCount,
          radius: fontSettings?.radius?.value || 0,
          startAngle: fontSettings?.start_angle?.value || 0,
          fabricProps: fontSettings?.word_count?.fabric_props || {},
          letterSpacing: circleLetterSpacing
        };
        fragment.append(this.buildTextField(clonedComponent, sideName, `${componentIndex}-${i}`, sortOrder, personalization));
      }
    }

    return fragment;
  },

  // Render every supported component for one side of the product.
  renderSide: function (personalization, sideName, components, sortOrder, clearFields = true) {
    const fields = personalization.querySelector('[data-byo-personalization-fields]');

    if ( clearFields ) {
      fields.replaceChildren();
    }

    for (let i = 0; i < components.length; i += 1) {
      const component = components[i];

      if ( component?.type === 'text' ) {
        fields.append(this.buildTextField(component, sideName, i, sortOrder, personalization));
      } else if ( component?.type === 'gemstones' ) {
        fields.append(this.buildGemstones(component, sideName, i, sortOrder, personalization));
      } else if ( component?.type === 'circletext' ) {
        fields.append(this.buildCircleText(component, sideName, i, sortOrder, personalization));
      }
    }
  },

  // Build or rebuild a modal form for the currently selected product variant.
  buildForm: function (personalization) {
    const personalizationData = this.readJson(personalization, '[data-byo-personalization-json]', []);
    const sortOrder = this.readJson(personalization, '[data-byo-personalization-sort-order]', {});
    const currentOptions = this.getCurrentOptions(personalization);
    const item = personalizationData[0] || {};
    const frontComponents = this.findSideData(item.front, currentOptions);
    const backComponents = this.findSideData(item.back, currentOptions);
    const fields = personalization.querySelector('[data-byo-personalization-fields]');
    const message = personalization.querySelector('[data-byo-personalization-message]');
    const modal = personalization.closest('x-modal');
    const addToBundleButton = modal?.querySelector('[data-byo-add-to-bundle]');

    if ( !fields || !message ) {
      return
    }

    personalization.dataset.currentOptions = JSON.stringify(currentOptions);
    const hasConfiguredPersonalization = this.hasPersonalizationComponents(item.front) || this.hasPersonalizationComponents(item.back);
    const personalizationWasDeclined = this.hasPersonalizationOptOut(currentOptions);

    if ( personalizationWasDeclined || (frontComponents.length === 0 && backComponents.length === 0) ) {
      const allowWithoutPersonalization = !hasConfiguredPersonalization || personalizationWasDeclined;

      this.disposePreviews(personalization);
      fields.replaceChildren();
      personalization.removeAttribute('data-byo-personalization-ready');

      if ( allowWithoutPersonalization ) {
        message.hidden = true;
        personalization.dataset.byoValidationRequired = 'false';

        if ( addToBundleButton ) {
          addToBundleButton.hidden = false;
        }

        this.validateForm(personalization);

        return
      }

      message.textContent = 'Personalization is not available for this option.';
      message.hidden = false;

      if ( addToBundleButton ) {
        addToBundleButton.hidden = true;
      }

      personalization.dataset.byoValidationRequired = 'true';
      this.validateForm(personalization);

      return
    }

    message.hidden = true;

    if ( addToBundleButton ) {
      addToBundleButton.hidden = false;
    }

    personalization.dataset.byoValidationRequired = 'true';

    if ( frontComponents.length > 0 ) {
      this.renderSide(personalization, 'front', frontComponents, sortOrder, true);
    } else {
      fields.replaceChildren();
    }

    if ( backComponents.length > 0 ) {
      const backLabel = document.createElement('div');
      backLabel.className = 'byo-personalization__back-label';
      backLabel.textContent = personalization.dataset.backLabel;
      fields.append(backLabel);
      this.renderSide(personalization, 'back', backComponents, sortOrder, false);
    }

    this.buildPreviews(personalization, frontComponents, backComponents);
    this.validateForm(personalization);

    personalization.dataset.byoPersonalizationReady = 'true';
    personalization.dispatchEvent(new CustomEvent('byo:personalization-ready', {
      bubbles: true
    }));
  },

  // Find an add-on variant by its option title.
  findAddOnVariant: function (product, optionName) {
    const variants = product?.variants || [];
    const normalizedOptionName = String(optionName || '').trim().toLowerCase();
    let matchingVariant = null;

    for ( const variant of variants ) {
      const variantTitle = String(variant.title || '').trim().toLowerCase();

      if ( variantTitle === normalizedOptionName ) {
        matchingVariant = variant;
        break;
      }
    }

    return matchingVariant;
  },

  // Format an inline gemstone token the same way as the personalization drawer.
  formatInlineGemstoneValue: function (personalization, gemstoneName) {
    const drawerManager = this.getDrawerManager(personalization);
    const nameFormat = drawerManager?.gemstoneInlineNameFormat || 'stone';
    let formattedValue = `[${gemstoneName}]`;

    if ( nameFormat === 'stone' ) {
      const nameParts = gemstoneName.toLowerCase().split('-');
      const stoneName = nameParts.length > 1 ? nameParts[1].trim() : gemstoneName;
      formattedValue = `[${stoneName}]`;
    } else if ( nameFormat === 'sku' ) {
      const gemstoneSku = drawerManager?.iconAndGemstoneSkuMap?.[gemstoneName] || '';

      if ( gemstoneSku ) {
        formattedValue = gemstoneSku.toUpperCase();
      }
    }

    return formattedValue;
  },

  // Collect personalized field properties and any paid add-on products.
  getFormSelection: function (personalization) {
    const properties = {};
    const addOns = [];
    const inlineGemstones = {};
    const inlineContent = {};
    const textValues = {};
    const inputs = personalization.querySelectorAll('[data-byo-personalization-input]');
    let hasBackText = false;

    for ( const input of inputs ) {
      const inputValue = this.getInputValue(input).trim();
      let inputGemstones = [];
      let propertyValue = inputValue;
      const contentTokens = [];

      if ( input.dataset.byoInlineEditor === 'true' ) {
        propertyValue = '';

        for ( const childNode of input.childNodes ) {
          if ( childNode.nodeType === Node.ELEMENT_NODE && childNode.nodeName === 'IMG' && childNode.hasAttribute('data-byo-inline-gemstone') ) {
            const inputGemstone = {
              name: childNode.getAttribute('alt') || '',
              imageUrl: childNode.getAttribute('src') || '',
              characterCount: Number(childNode.dataset.characterCount || 1)
            };

            inputGemstones.push(inputGemstone);
            contentTokens.push({
              type: 'gemstone',
              ...inputGemstone
            });
            propertyValue += this.formatInlineGemstoneValue(personalization, inputGemstone.name);
          } else if ( childNode.nodeType === Node.ELEMENT_NODE && childNode.nodeName === 'IMG' && childNode.hasAttribute('data-byo-inline-symbol') ) {
            const symbolName = childNode.getAttribute('alt') || '';
            const symbolImageUrl = childNode.getAttribute('src') || '';
            const drawerManager = this.getDrawerManager(personalization);
            const symbolSku = drawerManager?.iconAndGemstoneSkuMap?.[symbolName] || '';

            contentTokens.push({
              type: 'symbol',
              name: symbolName,
              imageUrl: symbolImageUrl,
              characterCount: Number(childNode.dataset.characterCount || 1)
            });
            propertyValue += symbolSku ? `[${symbolSku}]` : `[${symbolName}]`;
          } else {
            const textContent = childNode.textContent || '';

            if ( textContent ) {
              contentTokens.push({
                type: 'text',
                value: textContent
              });
              propertyValue += textContent;
            }
          }
        }
      }

      if ( !inputValue && inputGemstones.length === 0 && contentTokens.length === 0 ) {
        continue;
      }

      const fieldLabel = input.dataset.fieldLabel.replace(/:$/, '');
      const casePropertyPrefix = Number(input.dataset.caseOptionCount || 0) <= 1 ? '_' : '';
      const alignmentPropertyPrefix = Number(input.dataset.alignmentOptionCount || 0) <= 1 ? '_' : '';

      properties[fieldLabel] = propertyValue;
      properties[`${casePropertyPrefix}${fieldLabel} - Upper/Lower Limits`] = input.dataset.case;
      textValues[fieldLabel] = inputValue;

      if ( inputGemstones.length > 0 ) {
        inlineGemstones[fieldLabel] = inputGemstones;
      }

      if ( input.dataset.byoInlineEditor === 'true' && contentTokens.length > 0 ) {
        inlineContent[fieldLabel] = contentTokens;
      }

      if ( input.dataset.alignment ) {
        properties[`${alignmentPropertyPrefix}${fieldLabel} - Text Alignment`] = input.dataset.alignment;
      }

      if ( input.dataset.side === 'back' ) {
        hasBackText = true;
      }
    }

    const gemstoneProduct = this.readJson(personalization, '[data-byo-gemstone-product]', null);
    const selectedGemstones = personalization.querySelectorAll('[data-personalization-type="gemstone"][aria-pressed="true"]');
    let standaloneGemstoneCount = 0;

    for ( const fieldLabel in inlineGemstones ) {
      const inputGemstones = inlineGemstones[fieldLabel];

      for ( const inputGemstone of inputGemstones ) {
        const gemstoneVariant = this.findAddOnVariant(gemstoneProduct, inputGemstone.name);

        if ( gemstoneVariant ) {
          addOns.push({
            variantId: gemstoneVariant.id,
            priceCents: Number(gemstoneVariant.price || 0),
            label: inputGemstone.name,
            type: 'gemstone',
            bundleWithProduct: true
          });
        }
      }
    }

    for ( const selectedGemstone of selectedGemstones ) {
      const gemstoneName = selectedGemstone.dataset.personalizationName;
      const circleGemstoneField = selectedGemstone.closest('[data-byo-circle-gemstone]');
      let gemstonePropertyName = 'Stone';

      if ( circleGemstoneField ) {
        const wordPosition = Number(circleGemstoneField.dataset.circleWordPosition || 0) + 1;
        gemstonePropertyName = `Stone ${wordPosition}`;
      } else {
        standaloneGemstoneCount += 1;

        if ( standaloneGemstoneCount > 1 ) {
          gemstonePropertyName = `Stone-${standaloneGemstoneCount}`;
        }
      }

      properties[gemstonePropertyName] = gemstoneName;

      const gemstoneVariant = this.findAddOnVariant(gemstoneProduct, gemstoneName);

      if ( !gemstoneVariant ) {
        continue;
      }

      addOns.push({
        variantId: gemstoneVariant.id,
        priceCents: Number(gemstoneVariant.price || 0),
        label: gemstoneName,
        type: 'gemstone'
      });
    }

    if ( hasBackText ) {
      const backEngravingProduct = this.readJson(personalization, '[data-byo-back-engraving-product]', null);
      const backEngravingVariant = backEngravingProduct?.variants?.[0] || null;

      if ( backEngravingVariant ) {
        addOns.push({
          variantId: backEngravingVariant.id,
          priceCents: Number(backEngravingVariant.price || 0),
          label: 'Back Engraving',
          type: 'back-engraving'
        });
      }
    }

    const formSelection = {
      properties,
      addOns,
      inlineGemstones,
      inlineContent,
      textValues
    };

    return formSelection;
  },

  // Read the saved personalization attached to this modal's selected BYO card.
  getSavedFormSelection: function (personalization) {
    const modal = personalization.closest('x-modal');
    const productCardId = modal?.dataset.byoOriginCardId;
    const productCard = productCardId ? document.getElementById(productCardId) : null;
    const dropdown = productCard?.closest('[data-byo-dropdown]');
    const selectionElement = dropdown?.querySelector('[data-byo-selection]');
    let savedPersonalization = null;

    try {
      const selection = JSON.parse(selectionElement?.dataset.selection || '{}');
      savedPersonalization = selection.personalization || null;
    } catch (error) {
      savedPersonalization = null;
    }

    return savedPersonalization;
  },
  // Restore saved text and option choices when an existing BYO product modal opens.
  restoreSavedForm: function (personalization) {
    const savedPersonalization = this.getSavedFormSelection(personalization);

    if ( !savedPersonalization ) {
      this.validateForm(personalization);
      return
    }

    const properties = savedPersonalization.properties || {};
    const inlineGemstones = savedPersonalization.inlineGemstones || {};
    const inlineContent = savedPersonalization.inlineContent || {};
    const textValues = savedPersonalization.textValues || {};
    const inputs = personalization.querySelectorAll('[data-byo-personalization-input]');

    for ( const input of inputs ) {
      const fieldLabel = input.dataset.fieldLabel.replace(/:$/, '');
      const casePropertyName = `${fieldLabel} - Upper/Lower Limits`;
      const privateCasePropertyName = `_${casePropertyName}`;
      const alignmentPropertyName = `${fieldLabel} - Text Alignment`;
      const privateAlignmentPropertyName = `_${alignmentPropertyName}`;
      const savedCase = properties[casePropertyName] || properties[privateCasePropertyName] || '';
      const savedAlignment = properties[alignmentPropertyName] || properties[privateAlignmentPropertyName] || '';

      const savedInputValue = textValues[fieldLabel] ?? properties[fieldLabel] ?? '';
      const normalizedSavedInputValue = this.normalizeTextHearts(savedInputValue);
      this.setInputValue(input, normalizedSavedInputValue);

      if ( input.dataset.byoInlineEditor === 'true' ) {
        const savedContentTokens = inlineContent[fieldLabel] || [];

        if ( savedContentTokens.length > 0 ) {
          input.replaceChildren();

          for ( const savedContentToken of savedContentTokens ) {
            if ( savedContentToken.type === 'gemstone' ) {
              const gemstoneImage = document.createElement('img');
              gemstoneImage.src = savedContentToken.imageUrl;
              gemstoneImage.alt = savedContentToken.name;
              gemstoneImage.dataset.byoInlineGemstone = '';
              gemstoneImage.dataset.symbolType = 'gemstone';
              gemstoneImage.dataset.characterCount = Number(savedContentToken.characterCount || 1);
              gemstoneImage.contentEditable = 'false';
              input.append(gemstoneImage);
            } else if ( savedContentToken.type === 'symbol' ) {
              const symbolImage = document.createElement('img');
              symbolImage.src = savedContentToken.imageUrl;
              symbolImage.alt = savedContentToken.name;
              symbolImage.dataset.byoInlineSymbol = '';
              symbolImage.dataset.symbolType = 'other';
              symbolImage.dataset.characterCount = Number(savedContentToken.characterCount || 1);
              symbolImage.contentEditable = 'false';
              input.append(symbolImage);
            } else if ( savedContentToken.type === 'text' ) {
              const savedText = this.normalizeTextHearts(savedContentToken.value || '');
              input.append(document.createTextNode(savedText));
            }
          }
        } else {
          const savedInlineGemstones = inlineGemstones[fieldLabel] || [];

          for ( const savedInlineGemstone of savedInlineGemstones ) {
            const gemstoneImage = document.createElement('img');
            gemstoneImage.src = savedInlineGemstone.imageUrl;
            gemstoneImage.alt = savedInlineGemstone.name;
            gemstoneImage.dataset.byoInlineGemstone = '';
            gemstoneImage.dataset.symbolType = 'gemstone';
            gemstoneImage.dataset.characterCount = Number(savedInlineGemstone.characterCount || 1);
            gemstoneImage.contentEditable = 'false';
            input.append(gemstoneImage);
          }
        }
      }

      if ( savedCase ) {
        input.dataset.case = savedCase;
      }

      if ( savedAlignment ) {
        input.dataset.alignment = savedAlignment;
      }

      input.dispatchEvent(new Event('input', { bubbles: true }));

      const choiceButtons = personalization.querySelectorAll('[data-byo-personalization-choice]');

      for ( const choiceButton of choiceButtons ) {
        const groupName = choiceButton.dataset.byoPersonalizationChoice;
        let shouldSelect = false;

        if ( groupName === `case-${input.id}` ) {
          shouldSelect = choiceButton.dataset.value.replace(' case', '') === savedCase;
        } else if ( groupName === `alignment-${input.id}` ) {
          shouldSelect = choiceButton.dataset.value === savedAlignment;
        } else {
          continue;
        }

        choiceButton.classList.toggle('is-selected', shouldSelect);
        choiceButton.classList.toggle('enabled-box', shouldSelect);
        choiceButton.setAttribute('aria-pressed', String(shouldSelect));
      }
    }

    const addOns = savedPersonalization.addOns || [];
    const gemstoneButtons = personalization.querySelectorAll('[data-personalization-type="gemstone"]');

    for ( const gemstoneButton of gemstoneButtons ) {
      let shouldSelect = false;
      const circleGemstoneField = gemstoneButton.closest('[data-byo-circle-gemstone]');

      if ( circleGemstoneField ) {
        const wordPosition = Number(circleGemstoneField.dataset.circleWordPosition || 0) + 1;
        const savedGemstoneName = properties[`Stone ${wordPosition}`] || '';
        shouldSelect = savedGemstoneName === gemstoneButton.dataset.personalizationName;
      } else {
        for ( const addOn of addOns ) {
          if ( addOn.type === 'gemstone' && addOn.label === gemstoneButton.dataset.personalizationName ) {
            shouldSelect = true;
            break;
          }
        }
      }

      gemstoneButton.classList.toggle('is-selected', shouldSelect);
      gemstoneButton.classList.toggle('enabled-box', shouldSelect);
      gemstoneButton.setAttribute('aria-pressed', String(shouldSelect));

      if ( gemstoneButton.closest('.byo-personalization__gemstone-dropdown') ) {
        gemstoneButton.setAttribute('aria-selected', String(shouldSelect));
      }
    }

    const standaloneGemstoneFields = personalization.querySelectorAll('[data-byo-gemstone-field]');

    for ( const standaloneGemstoneField of standaloneGemstoneFields ) {
      this.syncGemstoneDropdown(standaloneGemstoneField);
      this.updateStandaloneGemstonePreview(personalization, standaloneGemstoneField);
    }

    const circleGemstoneFields = personalization.querySelectorAll('[data-byo-circle-gemstone]');
    const updatedSides = [];

    for ( const circleGemstoneField of circleGemstoneFields ) {
      const adapterSelector = `[data-byo-circle-gemstone-adapter-for="${CSS.escape(circleGemstoneField.id)}"]`;
      const adapter = personalization.querySelector(adapterSelector);
      const sideName = circleGemstoneField.dataset.side;

      if ( adapter ) {
        this.syncCircleGemstoneAdapter(circleGemstoneField, adapter);
      }

      if ( updatedSides.indexOf(sideName) === -1 ) {
        updatedSides.push(sideName);
      }
    }

    for ( const sideName of updatedSides ) {
      this.updatePreviewSide(personalization, sideName);
    }

    this.validateForm(personalization);
  },

  // Handle form buttons and character counters inside one modal.
  bindForm: function (personalization) {
    personalization.addEventListener('pointerdown', (event) => {
      const inlineGemstoneField = event.target.closest('[data-byo-inline-gemstone-field]');
      const symbolChoice = event.target.closest('[data-byo-personalization-choice^="symbol-"]');
      const fieldId = inlineGemstoneField?.dataset.targetField || symbolChoice?.dataset.targetField || '';
      const input = fieldId ? personalization.querySelector(`#${CSS.escape(fieldId)}`) : null;

      if ( input?.dataset.byoInlineEditor === 'true' ) {
        this.saveInlineEditorSelection(input);
      }
    });

    personalization.addEventListener('click', (event) => {
      const inlineEditor = event.target.closest('[data-byo-inline-editor="true"]');

      if ( inlineEditor && personalization.contains(inlineEditor) ) {
        this.saveInlineEditorSelection(inlineEditor);
      }

      const gemstoneDropdownToggle = event.target.closest('[data-byo-gemstone-dropdown-toggle]');

      if ( gemstoneDropdownToggle && personalization.contains(gemstoneDropdownToggle) ) {
        const dropdown = gemstoneDropdownToggle.closest('.byo-personalization__gemstone-dropdown');
        const options = dropdown?.querySelector('.byo-personalization__gemstone-dropdown-options');
        const shouldOpen = gemstoneDropdownToggle.getAttribute('aria-expanded') !== 'true';

        gemstoneDropdownToggle.setAttribute('aria-expanded', String(shouldOpen));

        if ( options ) {
          options.hidden = !shouldOpen;
        }

        return;
      }

      const choice = event.target.closest('[data-byo-personalization-choice]');

      if ( choice && personalization.contains(choice) ) {
        if ( choice.hasAttribute('data-byo-inline-gemstone-choice') ) {
          this.handleInlineGemstoneChoice(personalization, choice);
          return;
        }

        const group = choice.dataset.byoPersonalizationChoice;
        const choices = personalization.querySelectorAll('[data-byo-personalization-choice]');

        for ( const button of choices ) {
          if ( button.dataset.byoPersonalizationChoice === group ) {
            const isSelected = button === choice;
            button.classList.toggle('is-selected', isSelected);
            button.classList.toggle('enabled-box', isSelected);
            button.setAttribute('aria-pressed', String(isSelected));
          }
        }

        if ( group.startsWith('case-') ) {
          const fieldId = group.replace('case-', '');
          const input = personalization.querySelector(`#${CSS.escape(fieldId)}`);

          if ( input ) {
            const normalizedCase = choice.dataset.value.replace(' case', '');
            const caseChanged = input.dataset.case !== normalizedCase;

            if ( caseChanged ) {
              this.setInputValue(input, '');
              this.inlineSelectionState.delete(input);
            }

            input.dataset.case = normalizedCase;
            this.updateAutocapitalize(input);
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }

        if ( group.startsWith('alignment-') ) {
          const fieldId = group.replace('alignment-', '');
          const input = personalization.querySelector(`#${CSS.escape(fieldId)}`);

          if ( input ) {
            input.dataset.alignment = choice.dataset.value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }

        if ( group.startsWith('symbol-') && (choice.dataset.symbol || choice.dataset.symbolImage) ) {
          const input = personalization.querySelector(`#${CSS.escape(choice.dataset.targetField)}`);

          const inputValue = input ? this.getInputValue(input) : '';
          const characterCount = input ? this.getPersonalizationCharacterCount(input) : 0;
          const symbolCharacterCount = Number(choice.dataset.characterCount || 1);

          if ( input && characterCount + symbolCharacterCount <= input.maxLength ) {
            if ( input.dataset.byoInlineEditor === 'true' ) {
              let symbolNode = null;

              if ( choice.dataset.symbolImage ) {
                const symbolImage = document.createElement('img');
                symbolImage.src = choice.dataset.symbolImage;
                symbolImage.alt = choice.dataset.personalizationName || '';
                symbolImage.dataset.byoInlineSymbol = '';
                symbolImage.dataset.symbolType = 'other';
                symbolImage.dataset.characterCount = symbolCharacterCount;
                symbolImage.contentEditable = 'false';
                symbolNode = symbolImage;
              } else {
                const symbolText = this.normalizeTextHearts(choice.dataset.symbol);
                symbolNode = document.createTextNode(symbolText);
              }

              this.insertAtInlineEditorCaret(input, symbolNode);
            } else {
              const symbolText = this.normalizeTextHearts(choice.dataset.symbol);
              this.setInputValue(input, inputValue + symbolText);
            }

            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }

        if ( group.startsWith('circle-gemstone-') || group.startsWith('gemstones-') ) {
          const gemstoneField = choice.closest('[data-byo-circle-gemstone], [data-byo-gemstone-field]');
          const circleGemstoneField = choice.closest('[data-byo-circle-gemstone]');
          const adapterSelector = `[data-byo-circle-gemstone-adapter-for="${CSS.escape(gemstoneField?.id || '')}"]`;
          const adapter = personalization.querySelector(adapterSelector);

          for ( const button of choices ) {
            if ( button.dataset.byoPersonalizationChoice === group ) {
              button.setAttribute('aria-selected', String(button === choice));
            }
          }

          if ( gemstoneField ) {
            this.syncGemstoneDropdown(gemstoneField);

            const dropdownToggle = gemstoneField.querySelector('[data-byo-gemstone-dropdown-toggle]');

            if ( dropdownToggle ) {
              dropdownToggle.focus();
            }
          }

          if ( circleGemstoneField && adapter ) {
            this.syncCircleGemstoneAdapter(circleGemstoneField, adapter);
            this.updatePreviewSide(personalization, circleGemstoneField.dataset.side);
            this.showPreview(personalization, circleGemstoneField.dataset.side);
          } else if ( gemstoneField ) {
            this.updateStandaloneGemstonePreview(personalization, gemstoneField);
            this.showPreview(personalization, gemstoneField.dataset.side);
          }
        }

        this.validateForm(personalization);
      }
    });

    personalization.addEventListener('input', (event) => {
      const input = event.target.closest('[data-byo-personalization-input]');

      if ( !input ) {
        return
      }

      if ( event.isComposing || this.composingInputs.has(input) ) {
        this.updateCharacterLimit(personalization, input);
        this.updatePreviewForInput(personalization, input);

        if ( input.dataset.byoInlineEditor === 'true' ) {
          this.saveInlineEditorSelection(input);
        }

        this.validateForm(personalization);

        return;
      }

      this.applyInputCase(input, input.dataset.case);

      this.updateCharacterLimit(personalization, input);

      this.updatePreviewForInput(personalization, input);

      if ( input.dataset.byoInlineEditor === 'true' ) {
        this.saveInlineEditorSelection(input);
      }

      this.validateForm(personalization);
    });

    personalization.addEventListener('compositionstart', (event) => {
      const input = event.target.closest('[data-byo-personalization-input]');

      if ( input ) {
        this.composingInputs.add(input);
      }
    });

    personalization.addEventListener('compositionend', (event) => {
      const input = event.target.closest('[data-byo-personalization-input]');

      if ( !input ) {
        return;
      }

      window.setTimeout(() => {
        this.composingInputs.delete(input);
        this.applyInputCase(input, input.dataset.case);
        this.updateCharacterLimit(personalization, input);
        this.updatePreviewForInput(personalization, input);

        if ( input.dataset.byoInlineEditor === 'true' ) {
          this.saveInlineEditorSelection(input);
        }

        this.validateForm(personalization);
      }, 0);
    });

    personalization.addEventListener('keyup', (event) => {
      const input = event.target.closest('[data-byo-inline-editor="true"]');

      if ( input ) {
        this.saveInlineEditorSelection(input);
      }
    });

    personalization.addEventListener('keydown', (event) => {
      const input = event.target.closest('[data-byo-inline-editor="true"]');

      if ( input && event.key === 'Enter' ) {
        event.preventDefault();
      }
    });

    personalization.addEventListener('focusin', (event) => {
      const input = event.target.closest('[data-byo-personalization-input]');

      if ( input ) {
        this.updatePreviewForInput(personalization, input);
      }
    });
  },

  // Initialize every BYO personalization form and rebuild it after variant changes.
  init: function () {
    const forms = document.querySelectorAll('[data-byo-personalization]');

    for ( const personalization of forms ) {
      if ( personalization.dataset.byoPersonalizationBound !== 'true' ) {
        personalization.dataset.byoPersonalizationBound = 'true';
        personalization.id ||= `ByoPersonalization-${Math.random().toString(36).slice(2)}`;
        this.bindForm(personalization);
      }

      if ( personalization.dataset.byoPersonalizationLoaded === 'true' ) {
        this.buildForm(personalization);
      }
    }
  }
};

window.byoPersonalizationManager = byoPersonalizationManager;

document.addEventListener('click', (event) => {
  const previewTab = event.target.closest('[data-byo-preview-tab]');

  if ( !previewTab ) {
    return;
  }

  const modal = previewTab.closest('x-modal');
  const personalization = modal?.querySelector('[data-byo-personalization]');

  if ( personalization ) {
    byoPersonalizationManager.showPreview(personalization, previewTab.dataset.byoPreviewTab);
  }
});

document.addEventListener('keydown', (event) => {
  const previewTab = event.target.closest('[data-byo-preview-tab]');

  if ( !previewTab || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') ) {
    return;
  }

  const tabList = previewTab.closest('[data-byo-preview-tabs]');
  const visibleTabs = tabList?.querySelectorAll('[data-byo-preview-tab]:not([hidden])') || [];
  let currentIndex = -1;

  for ( let i = 0; i < visibleTabs.length; i += 1 ) {
    if ( visibleTabs[i] === previewTab ) {
      currentIndex = i;
      break;
    }
  }

  if ( currentIndex < 0 || visibleTabs.length === 0 ) {
    return;
  }

  event.preventDefault();
  const direction = event.key === 'ArrowRight' ? 1 : -1;
  const nextIndex = (currentIndex + direction + visibleTabs.length) % visibleTabs.length;
  visibleTabs[nextIndex].click();
  visibleTabs[nextIndex].focus();
});

document.addEventListener('click', (event) => {
  const productCard = event.target.closest('[data-byo-product-card]');

  if ( !productCard ) {
    return
  }

  const modalId = productCard.dataset.byoModalId;
  const modal = modalId ? document.getElementById(modalId) : null;
  const personalization = modal?.querySelector('[data-byo-personalization]');

  if ( personalization ) {
    byoPersonalizationManager.loadForm(personalization);
  }
});

document.addEventListener('change', (event) => {
  if ( !event.target.matches('[data-option-position]') ) {
    return
  }

  const personalization = event.target.closest('x-modal')?.querySelector('[data-byo-personalization]');

  if ( personalization?.dataset.byoPersonalizationLoaded === 'true' ) {
    window.setTimeout(() => {
      byoPersonalizationManager.buildForm(personalization);
      byoPersonalizationManager.restoreSavedForm(personalization);
    }, 0);
  }
});

document.addEventListener('shopify:section:load', () => {
  byoPersonalizationManager.init();
});

document.addEventListener('DOMContentLoaded', () => {
  byoPersonalizationManager.init();
});
